import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Flame } from "lucide-react";
import { toast } from "sonner";
import { Separator } from "@/components/ui/separator";
import { isValidEmail, sanitize } from "@/lib/validation";
import { getAuthRedirectBase } from "@/lib/auth-redirect";
import { useAuditLog } from "@/hooks/useAuditLog";
import { savePendingInviteToken, redeemInviteToken, clearPendingInviteToken, describeInviteError } from "@/lib/inviteToken";
import { waitForSession, ensureProfile } from "@/lib/oauth-helpers";
import { getAuthErrorMessage } from "@/lib/auth-errors";
import { setRememberMe } from "@/lib/auth-storage";
import { Checkbox } from "@/components/ui/checkbox";

const MAX_ATTEMPTS = 5;
const LOCKOUT_MS = 60_000;

async function isAdminUser(): Promise<boolean> {
  const { data: userData } = await supabase.auth.getUser();
  if (!userData?.user) return false;
  const { data } = await supabase
    .from("user_roles" as any)
    .select("role")
    .eq("user_id", userData.user.id)
    .in("role", ["admin", "super_admin"]);
  return !!data && data.length > 0;
}

export default function Login() {
  const [searchParams] = useSearchParams();
  const inviteToken = searchParams.get("invite");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMeState] = useState(true);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [attempts, setAttempts] = useState(0);
  const [lockedUntil, setLockedUntil] = useState<number | null>(null);
  const navigate = useNavigate();
  const { logAction } = useAuditLog();

  useEffect(() => {
    if (inviteToken) savePendingInviteToken(inviteToken);
  }, [inviteToken]);

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!email.trim()) errs.email = "Email is required";
    else if (!isValidEmail(email.trim())) errs.email = "Enter a valid email address";
    if (!password) errs.password = "Password is required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  const isLockedOut = lockedUntil !== null && Date.now() < lockedUntil;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;
    if (isLockedOut) {
      const secs = Math.ceil((lockedUntil! - Date.now()) / 1000);
      toast.error(`Too many attempts. Try again in ${secs}s.`);
      return;
    }
    setLoading(true);
    setRememberMe(rememberMe);

    // Server-side brute-force gate — the client-side lockout below is only
    // local UX and can't stop someone hitting the auth API directly. This
    // call is the real, unbypassable-from-the-browser enforcement.
    const cleanEmail = sanitize(email);
    const { error: guardError } = await supabase.functions.invoke("login-guard", { body: { email: cleanEmail } });
    if (guardError) {
      let retryAfter = 60;
      try {
        const body = await (guardError as any).context?.json?.();
        if (body?.retry_after_seconds) retryAfter = Number(body.retry_after_seconds);
      } catch { /* fall back to default */ }
      setLoading(false);
      toast.error(`Too many attempts. Try again in ${retryAfter}s.`);
      return;
    }

    const { error } = await supabase.auth.signInWithPassword({ email: cleanEmail, password });
    setLoading(false);
    if (error) {
      // Email-not-confirmed is not a credential failure — the credentials
      // were correct, the account just isn't active yet. Send them straight
      // to the same "check your email" flow signup uses (with a fresh
      // confirmation email) instead of showing a generic invalid-credentials
      // error, which would wrongly suggest the password was wrong.
      const isUnconfirmed =
        (error as any)?.code === "email_not_confirmed" ||
        /email.*not.*confirm/i.test(error.message);
      if (isUnconfirmed) {
        const confirmEmail = sanitize(email);
        void supabase.auth.resend({
          type: "signup",
          email: confirmEmail,
          options: { emailRedirectTo: getAuthRedirectBase() },
        });
        toast.info("Your account isn't confirmed yet — we've sent a new confirmation link.");
        navigate("/check-email", { state: { email: confirmEmail } });
        return;
      }
      // Rate limiting isn't a credential failure — don't count it toward
      // the lockout, just relay the wait time to the user.
      const isRateLimited = error.status === 429 || /rate limit/i.test(error.message);
      if (isRateLimited) {
        toast.error(getAuthErrorMessage(error));
        return;
      }

      const next = attempts + 1;
      setAttempts(next);
      if (next >= MAX_ATTEMPTS) {
        setLockedUntil(Date.now() + LOCKOUT_MS);
        toast.error("Too many failed attempts. Locked for 60 seconds.");
        setTimeout(() => { setAttempts(0); setLockedUntil(null); }, LOCKOUT_MS);
      } else {
        toast.error(getAuthErrorMessage(error));
      }
    } else {
      setAttempts(0);
      logAction("login", "auth");
      // Redeem any pending invite before routing.
      if (inviteToken) {
        const result = await redeemInviteToken(inviteToken);
        if (result.ok) {
          clearPendingInviteToken();
          toast.success("Welcome to the team!");
          navigate(result.role === "staff" ? "/tasks" : "/");
          return;
        }
        const message = result.error ? describeInviteError(result.error) : null;
        if (message) toast.warning(message);
      }
      navigate((await isAdminUser()) ? "/admin" : "/");
    }
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-xl bg-primary">
            <Flame className="h-6 w-6 text-primary-foreground" />
          </div>
          <CardTitle className="text-2xl">Sign in to LegacyGrowth</CardTitle>
          <p className="text-sm text-muted-foreground">Home Care Lead Management</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} required maxLength={255} />
              {errors.email && <p className="text-xs text-destructive">{errors.email}</p>}
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <PasswordInput id="password" value={password} onChange={e => setPassword(e.target.value)} required maxLength={128} />
              {errors.password && <p className="text-xs text-destructive">{errors.password}</p>}
            </div>
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer">
                <Checkbox
                  checked={rememberMe}
                  onCheckedChange={checked => setRememberMeState(checked === true)}
                />
                Remember me
              </label>
              <Link to="/forgot-password" className="text-xs text-primary hover:underline">
                Forgot password?
              </Link>
            </div>
            <Button type="submit" className="w-full" disabled={loading || isLockedOut}>
              {loading ? "Signing in…" : isLockedOut ? "Locked — try again soon" : "Sign In"}
            </Button>
          </form>
          <div className="my-4 flex items-center gap-2">
            <Separator className="flex-1" />
            <span className="text-xs text-muted-foreground">OR</span>
            <Separator className="flex-1" />
          </div>
          <Button
            variant="outline"
            className="w-full"
            disabled={loading}
            onClick={async () => {
              setLoading(true);
              if (inviteToken) savePendingInviteToken(inviteToken);
              const { error } = await supabase.auth.signInWithOAuth({
                provider: "google",
                options: {
                  redirectTo: getAuthRedirectBase(),
                },
              });
              if (error) {
                toast.error(getAuthErrorMessage(error) || "Google sign-in failed");
                setLoading(false);
                return;
              }
              const ok = await waitForSession();
              if (!ok) {
                toast.error("Sign-in didn't complete. Please try again.");
                setLoading(false);
                return;
              }
              await ensureProfile();
              navigate((await isAdminUser()) ? "/admin" : "/");
            }}
          >
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Continue with Google
          </Button>
          <p className="mt-4 text-center text-sm text-muted-foreground">
            Don't have an account?{" "}
            <Link to="/signup" className="font-medium text-primary hover:underline">Sign up</Link>
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
